/**
 * Chart color palette that respects light/dark mode.
 * Uses CSS custom properties defined in globals.css.
 * Falls back to light-mode values if CSS vars are unavailable.
 */

const FALLBACKS = {
  chart1: '#0F2341',
  chart2: '#2563EB',
  chart3: '#93C5FD',
  chart4: '#06B6D4',
  chart5: '#1A3A5C',
};

function getCSSVar(name: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback;
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
}

/** Read current chart palette from CSS variables (theme-aware). */
export function getChartColors() {
  return {
    chart1: getCSSVar('--chart-1', FALLBACKS.chart1),
    chart2: getCSSVar('--chart-2', FALLBACKS.chart2),
    chart3: getCSSVar('--chart-3', FALLBACKS.chart3),
    chart4: getCSSVar('--chart-4', FALLBACKS.chart4),
    chart5: getCSSVar('--chart-5', FALLBACKS.chart5),
  };
}

/** Ordered palette array — same order as PALETTE constants used throughout. */
export function getChartPalette(): string[] {
  const c = getChartColors();
  return [c.chart1, c.chart2, c.chart3, c.chart4, c.chart5];
}

/**
 * Extended 10-color palette for ring charts, treemaps, etc. Dark-mode aware.
 */
export function getExtendedPalette(): string[] {
  const isDark = typeof document !== 'undefined' &&
    document.documentElement.getAttribute('data-theme') === 'dark';
  return isDark
    ? ['#79C0FF', '#388BFD', '#58A6FF', '#1F6FEB', '#8B949E', '#56D364', '#3FB950', '#D2A8FF', '#BC8CFF', '#F778BA']
    : ['#0F2341', '#1E3A5F', '#2563EB', '#3B82F6', '#60A5FA', '#93C5FD', '#BFDBFE', '#1D4ED8', '#1E40AF', '#DBEAFE'];
}

/**
 * Ordinal DPD ramp: 5 severity steps of one blue hue, light (current) → dark
 * (90+). The dark-theme ramp stops at a lighter step because the darkest blue
 * sinks into the dark surface.
 */
export function getDpdRamp(): string[] {
  // Theme is flagged via the data-theme attribute (useTheme), not a class.
  const isDark = typeof document !== 'undefined' &&
    document.documentElement.getAttribute('data-theme') === 'dark';
  return isDark
    ? ['#B7D3F6', '#6DA7EC', '#3987E5', '#256ABF', '#184F95']
    : ['#86B6EF', '#5598E7', '#2A78D6', '#1C5CAB', '#0D366B'];
}

/**
 * Categorical palette for the analytics surfaces. Chips/legends provide the
 * secondary encoding for the closest pair of hues.
 */
export function getLuminousPalette(): string[] {
  const isDark = typeof document !== 'undefined' &&
    document.documentElement.getAttribute('data-theme') === 'dark';
  return isDark
    ? ['#3B82F6', '#0D9488', '#8B5CF6', '#EC4899']
    : ['#2563EB', '#0D9488', '#7C3AED', '#DB2777'];
}

/** Single-series bar accent for the Bridge-style analytics cards. */
export function getBarAccent(): string {
  const isDark = typeof document !== 'undefined' &&
    document.documentElement.getAttribute('data-theme') === 'dark';
  return isDark ? '#38BDF8' : '#0284C7';
}

/** Hover step of the bar accent. */
export function getBarAccentBright(): string {
  const isDark = typeof document !== 'undefined' &&
    document.documentElement.getAttribute('data-theme') === 'dark';
  return isDark ? '#7DD3FC' : '#38BDF8';
}
